import { useEffect, useRef, useState, type FC, type KeyboardEvent } from 'react';
import { LuArrowUp } from 'react-icons/lu';
/* O CSS é o da tela do assistente (a Home e a conversa importam o mesmo).
   Importar aqui também deixa o componente completo por si — o bundler
   resolve o arquivo uma vez só. */
import '../pages/AssistenteLayout.css';

interface Props {
    onEnviar: (texto: string) => void;
    enviando?: boolean;
    autoFocus?: boolean;
    placeholder?: string;
}

/**
 * O campo de pergunta do assistente, igual na Home e na conversa.
 *
 * <para><b>Sem seletor de alcance (18/09).</b> Havia aqui um seletor
 * "Empresa / Minha carteira". Ele saiu a pedido do cliente: *"não faz sentido
 * deixar um select para escolher se o chat pesquisa sobre a empresa ou a
 * carteira… tem que deixar sem nada e aí o agente pega até onde o perfil dele
 * alcança"*. E ele tem razão pelo que está medido: para quem não é
 * administrador o seletor não mudava nada — o alcance já era o do perfil —, e
 * para o administrador ele só servia para ver menos do que pode. Controle que
 * não muda o resultado para quase ninguém é ruído.</para>
 *
 * <para><b>Sem o "O que eu consigo ver" (24/09).</b> No lugar do seletor tinha
 * ficado um link com a lista do que o assistente consulta e do que não
 * consulta. Ele saiu a pedido do cliente. O pé do campo agora tem só o botão
 * de enviar.</para>
 */
const AssistenteComposer: FC<Props> = ({
    onEnviar,
    enviando = false,
    autoFocus = false,
    placeholder = 'Pergunte qualquer coisa…',
}) => {
    const [texto, setTexto] = useState('');
    const [focado, setFocado] = useState(false);

    const refTexto = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (autoFocus) refTexto.current?.focus();
    }, [autoFocus]);

    /* Cresce com o texto até o teto do CSS. Sem o reset para 'auto' antes de
       medir, o campo só cresce e nunca volta ao apagar uma linha. */
    useEffect(() => {
        const campo = refTexto.current;
        if (!campo) return;
        campo.style.height = 'auto';
        campo.style.height = `${campo.scrollHeight}px`;
    }, [texto]);

    const podeEnviar = texto.trim().length > 0 && !enviando;

    const enviar = () => {
        if (!podeEnviar) return;
        onEnviar(texto.trim());
        setTexto('');
    };

    /* Enter envia, Shift+Enter quebra linha. */
    const aoTeclar = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            enviar();
        }
    };

    return (
        <div className={`as-cmp ${focado ? 'is-focado' : ''}`}>
            <textarea
                ref={refTexto}
                className="as-ta"
                value={texto}
                placeholder={placeholder}
                rows={1}
                maxLength={2000}
                onChange={e => setTexto(e.target.value)}
                onKeyDown={aoTeclar}
                onFocus={() => setFocado(true)}
                onBlur={() => setFocado(false)}
                disabled={enviando}
            />

            <div className="as-cmp-pe">
                <button
                    type="button"
                    className="as-enviar"
                    onClick={enviar}
                    disabled={!podeEnviar}
                    aria-label="Enviar pergunta"
                    title="Enviar (Enter)"
                >
                    <LuArrowUp size={16} />
                </button>
            </div>
        </div>
    );
};

export default AssistenteComposer;
